import { useMemo, useState, memo } from "react";
import { Search, ChevronDown, Loader2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { useInfiniteBrands } from "@/hooks/useInfiniteBrands";

interface BrandFilterProps {
  title: string;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  onCommit?: () => void;
  placeholder?: string;
  className?: string;
}

export const BrandFilter = memo(
  ({
    title,
    selectedValues,
    onChange,
    onCommit,
    placeholder = "Select brand...",
    className,
  }: BrandFilterProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const {
      brands,
      isPending,
      isFetchingNextPage,
      hasNextPage,
      handleScrollEnd,
    } = useInfiniteBrands({
      search: searchQuery,
      enabled: isOpen,
      source: "jetsik_brands",
    });

    const handleOpenChange = (open: boolean) => {
      setIsOpen(open);
      if (!open && onCommit) {
        onCommit();
      }
    };

    const handleToggleOption = (option: string, checked: boolean) => {
      if (checked) {
        onChange([...selectedValues, option]);
      } else {
        onChange(selectedValues.filter((val) => val !== option));
      }
    };

    const displayValue =
      selectedValues.length > 0
        ? `${selectedValues.length} brands selected`
        : placeholder;

    const allOptions = useMemo(() => {
      const loadedNames = brands.map((brand) => brand.name);
      return Array.from(new Set([...loadedNames, ...selectedValues])).sort();
    }, [brands, selectedValues]);

    const isLoading = isPending || isFetchingNextPage;
    const hasSelection = selectedValues.length > 0;

    return (
      <div className={cn("flex flex-col gap-1.5", className)}>
        <label className="text-xs font-medium text-muted-foreground">
          {title}
        </label>

        <Popover open={isOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                "flex w-full cursor-pointer items-center justify-between rounded-[18px] border border-border bg-muted/60 px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted focus:outline-none focus:ring-1 focus:ring-ring",
                hasSelection && "font-medium text-foreground",
              )}
            >
              <span className="truncate">{displayValue}</span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </button>
          </PopoverTrigger>

          <PopoverContent
            className="w-[280px] rounded-xl border-border p-2"
            align="start"
          >
            <div className="sticky top-0 mb-2 flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search brand..."
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {isLoading && (
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              )}
            </div>

            <div
              className="hide-scrollbar max-h-[260px] space-y-0.5 overflow-y-auto px-1"
              onScroll={handleScrollEnd}
            >
              {allOptions.length === 0 && !isPending ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  No brands found.
                </div>
              ) : (
                allOptions.map((option) => {
                  const isSelected = selectedValues.includes(option);
                  return (
                    <div
                      key={option}
                      className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-muted"
                      onClick={() => handleToggleOption(option, !isSelected)}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => {}}
                        className="rounded-[4px] border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                      />
                      <span
                        className={cn(
                          "flex-1 truncate text-sm select-none",
                          isSelected
                            ? "font-medium text-foreground"
                            : "text-foreground/80",
                        )}
                      >
                        {option}
                      </span>
                    </div>
                  );
                })
              )}

              {(isPending || isFetchingNextPage || hasNextPage) && (
                <div className="py-2 text-center text-xs text-muted-foreground">
                  {isPending || isFetchingNextPage ? (
                    <Loader2 className="mx-auto h-3 w-3 animate-spin" />
                  ) : (
                    "Scroll to load more..."
                  )}
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);

BrandFilter.displayName = "BrandFilter";
